import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search as SearchIcon, X, CalendarDays } from "lucide-react";
import { t as T } from "@/lib/i18n";

// Date field with a clean calendar icon: the native (often ugly/clipped)
// indicator is stretched invisibly over the whole box so clicking anywhere
// still opens the picker, while our crisp icon shows on the right.
function DateBox({ value, onChange, title, testid, fluid = false }) {
  return (
    <div className={`relative ${fluid ? "min-w-0 flex-1" : ""}`}>
      <Input
        type="date"
        value={value}
        onChange={onChange}
        title={title}
        data-testid={testid}
        className={`h-9 rounded-sm ${fluid ? "w-full min-w-0 pl-2 pr-7 text-xs" : "w-[150px] pr-9 text-sm"} cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`}
      />
      <CalendarDays className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
    </div>
  );
}

// Word-AND match across everything a user can read on a ticket:
// title, details, note replies + authors, creator, assignees, colony, status.
export function ticketMatches(t, query, { colonyName = () => "", assigneeName = () => "", statusLabel = () => "" } = {}) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = [
    t.title, t.description, colonyName(t.colony_id),
    t.created_by_name, t.created_by_email, statusLabel(t.status),
    ...(t.assignees || []).map(assigneeName),
    ...(t.notes || []).flatMap((n) => [n.body, n.author_name]),
  ].filter(Boolean).join(" ").toLowerCase();
  return q.split(/\s+/).every((w) => hay.includes(w));
}

// Wraps every word of the active search query in a glowing <mark> so
// users spot the hit instantly inside each ticket card. Also turns any
// bare `http(s)://…` URL in the text into a clickable blue link opening
// in a new tab — requested so an admin can paste a Google Maps or docs
// link into a ticket note and have it clickable for the worker.
const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)"'\]])/gi;

// US-style phone numbers become tap-to-call links — (845) 123-4567,
// 845-123-4567, 845.123.4567, +1 845 123 4567 and bare 8451234567.
const PHONE_RE = /((?:\+?1[\s.-]?)?(?:\(\d{3}\)[\s.-]?|[2-9]\d{2}[\s.-])\d{3}[\s.-]?\d{4}(?!\d)|(?<!\d)\+?1?[2-9]\d{9}(?!\d))/g;

// Email addresses open the mail app the same way.
const EMAIL_RE = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

function _linkifyEmails(chunk, words, testIdPrefix) {
  const parts = String(chunk).split(EMAIL_RE);
  return parts.map((p, i) => {
    if (i % 2 === 1) {
      return (
        <a
          key={i}
          href={`mailto:${p}`}
          className="text-sky-700 underline decoration-sky-400 underline-offset-2 hover:text-sky-800 break-all"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-email-${i}`}
        >{p}</a>
      );
    }
    return <React.Fragment key={i}>{_linkifyPhones(p, words, `${testIdPrefix}-plain-${i}`)}</React.Fragment>;
  });
}

function _linkifyPhones(chunk, words, testIdPrefix) {
  const parts = String(chunk).split(PHONE_RE);
  return parts.map((p, i) => {
    if (i % 2 === 1) {
      const digits = p.replace(/\D/g, "");
      const tel = digits.length === 10 ? `+1${digits}` : `+${digits}`;
      return (
        <a
          key={i}
          href={`tel:${tel}`}
          className="text-sky-700 underline decoration-sky-400 underline-offset-2 hover:text-sky-800 whitespace-nowrap"
          onClick={(e) => e.stopPropagation()}
          data-testid={`${testIdPrefix}-phone-${i}`}
        >{p}</a>
      );
    }
    return <React.Fragment key={i}>{_highlightWords(p, words)}</React.Fragment>;
  });
}

function _highlightWords(text, words) {
  if (!words.length || !text) return text;
  const re = new RegExp(`(${words.join("|")})`, "gi");
  return String(text).split(re).map((p, i) =>
    i % 2 === 1
      ? <mark key={i} className="bg-yellow-200 text-slate-900 rounded-[2px] px-0.5 shadow-[0_0_6px_rgba(250,204,21,0.7)]">{p}</mark>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
}

export function Highlight({ text, query }) {
  const generatedId = React.useId().replace(/:/g, "");
  if (text == null || text === "") return text ?? null;
  const q = (query || "").trim();
  const words = q.split(/\s+/).filter(Boolean).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  // Split by URL first so links stay intact even when highlight words
  // happen to overlap the URL.
  const chunks = String(text).split(URL_RE);
  return chunks.map((chunk, i) => {
    if (URL_RE.test(chunk)) {
      // Reset lastIndex after `.test` (stateful global regex).
      URL_RE.lastIndex = 0;
      return (
        <a
          key={i}
          href={chunk}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sky-700 underline decoration-sky-400 underline-offset-2 hover:text-sky-800 break-all"
          onClick={(e) => e.stopPropagation()}
          data-testid={`linkified-${generatedId}-url-${i}`}
        >{chunk}</a>
      );
    }
    return <React.Fragment key={i}>{_linkifyEmails(chunk, words, `linkified-${generatedId}-chunk-${i}`)}</React.Fragment>;
  });
}

export function ticketInDates(t, from, to, { includeUpdated = false } = {}) {
  if (!from && !to) return true;
  const values = [t.created_at];
  if (includeUpdated) values.push(t.status_updated_at, t.updated_at, t.closed_at);
  return values.filter(Boolean).some((value) => {
    // Compare in LOCAL time — the same date the user sees on the ticket,
    // not the UTC timestamp which can be a day ahead.
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return false;
    const local = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    return (!from || local >= from) && (!to || local <= to);
  });
}

/** Compact search: a single icon button that expands into the
 * search box + created-date range when pressed. */
export function TicketSearchControl({ search, setSearch, dateFrom, setDateFrom, dateTo, setDateTo, testidPrefix = "tickets", placeholder = "Search title, details, notes, people…", fullWidthWhenOpen = false, collapseWhenSearchEmpty = false, preserveDatesOnClose = false, responsive = false }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const active = !!(search.trim() || (!collapseWhenSearchEmpty && (dateFrom || dateTo)));
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);
  if (!open && !active) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="h-9 w-9 grid place-items-center rounded-sm border border-border text-muted-foreground hover:text-foreground hover:border-foreground transition-colors shrink-0"
        title="Search tickets — title, details, notes, people, or a date"
        data-testid={`${testidPrefix}-search-toggle`}
      >
        <SearchIcon className="w-4 h-4" />
      </button>
    );
  }
  return (
    <div className={responsive ? "grid w-full min-w-0 grid-cols-[minmax(0,1fr)_2.25rem] items-center gap-2" : `${fullWidthWhenOpen ? "w-full " : ""}flex flex-wrap items-center gap-2`} data-testid={`${testidPrefix}-search-panel`}>
      <div className={responsive ? "relative col-span-2 min-w-0" : "relative min-w-[210px] flex-1 max-w-xs"}>
        <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={T(placeholder)}
          className="h-9 rounded-sm pl-8 pr-7"
          data-testid={`${testidPrefix}-search`}
        />
        {search && (
          <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid={`${testidPrefix}-search-clear`}>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className={`flex items-center gap-1.5 ${responsive ? "min-w-0" : ""}`}>
        <DateBox value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} title="Created from" testid={`${testidPrefix}-date-from`} fluid={responsive} />
        <span className="text-xs text-muted-foreground">to</span>
        <DateBox value={dateTo} onChange={(e) => setDateTo(e.target.value)} title="Created up to" testid={`${testidPrefix}-date-to`} fluid={responsive} />
      </div>
      <button
        type="button"
        onClick={() => {
          setSearch("");
          if (!preserveDatesOnClose) {
            setDateFrom("");
            setDateTo("");
          }
          setOpen(false);
        }}
        className="h-9 w-9 grid place-items-center rounded-sm border border-border text-muted-foreground hover:text-red-600 hover:border-red-300 transition-colors shrink-0"
        title={preserveDatesOnClose ? "Clear search & close — keep date range" : "Clear search & close"}
        data-testid={`${testidPrefix}-search-close`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
